import {
  Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit,
  Output, TemplateRef, ViewChild,
} from '@angular/core';
import {
  AbstractControl, FormArray, FormBuilder, FormGroup, FormsModule,
  ReactiveFormsModule, Validators,
} from '@angular/forms';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { scrollIntoView } from '@msslib/helpers/dom-helpers';
import { LendingTypeService } from '@msslib/services/lending-type.service';
import { ModalStateService } from '@msslib/services/modal-state.service';
import { ModalService } from '@msslib/services/modal.service';
import { ToastService } from '@msslib/services/toast.service';
import { Fee, FeeConfiguration, TariffOfCharges } from 'apps/usermanager/src/app/models/tariff-of-charges';
import { TariffOfChargesService } from 'apps/usermanager/src/app/services/tariff-of-charges.service';
import { Subject, debounceTime, distinctUntilChanged, pairwise, startWith, takeUntil } from 'rxjs';
import { TwoDigitDecimalNumberDirective } from '../../directives/two-digit-decimal-number.directive';
import { NgFor, NgIf } from '@angular/common';
import { SelectComponent } from '@msslib/components/select/select.component';

const FEE_TOOLTIPS: Record<string, string> = {
  'Arrangement fee': `Also known as Product fee or Application fee. This is the fee chargeable at mortgage application.
Your Arrangement fees are captured per product via your product upload sheet.`,
  'Booking fee': `Also known as Reservation fee. This is a fee charge to secure a mortgage deal.
Your Booking fees are captured per product via your product upload sheet.`,
  'CHAPS fee': 'Fee charged to transfer funds electronically.',
  'Conveyancing fee':`Estimated charge for standard legal work to complete mortgage transactions charged
  by your solicitors.`,
  'Deeds Release fee': `Also known as Release fee or Sealing fee. This is a fee charged to remove the charge
  on the property security.`,
  'Further advance fee': `Also known as Additional borrowing fee. This is a fee charged to process a further
  advance application.`,
  'Mortgage Exit fee': `Also known as Mortgage completion fee or Exit administration fee or Redemption
  administration fee. This is a fee charged to redeem a mortgage.`,
  'Valuation fee': `Fee charged to conduct your property valuation survey.
Valuation fees are calculated via your valuation fee scales.`,
};

@Component({
  selector: 'lib-lender-tariff-of-charges',
  templateUrl: './lender-tariff-of-charges.component.html',
  styleUrls: ['./lender-tariff-of-charges.component.scss'],
  standalone: true,
  imports: [
    NgFor,
    NgIf,
    FormsModule,
    ReactiveFormsModule,
    TwoDigitDecimalNumberDirective,
    SelectComponent,
    NgbTooltip,
  ],
})
export class LenderTariffOfChargesComponent implements OnInit, OnDestroy {
  @Input({ required: true }) public lenderNameId: number;
  @Input() public disabled = false;
  @Output() public unsavedChangesChange = new EventEmitter<boolean>();

  @ViewChild('lendingTypeModalTemplateRef', { static: true })
  public lendingTypeModalTemplateRef: TemplateRef<void>;
  @ViewChild('tableContainer')
  private tableContainer: ElementRef<HTMLElement>;

  public feeConfigOptions: { value: string; label: string }[] = [
    { value: 'purchase', label: 'Purchase' },
    { value: 'remortgage', label: 'Remortgage' },
    { value: 'productTransfer', label: 'Product Transfer' },
    { value: 'furtherAdvance', label: 'Further Advance' },
    { value: 'existingCustomer', label: 'Existing Customer' },
    { value: 'secondCharge', label: 'Second Charge' },
  ];

  public form: FormGroup;
  public model: TariffOfCharges | null;
  public lendingTypeOptions: string[] = [];
  public additionalFeeOptions: string[] = [];

  public get feeArray(): FormArray {
    return this.form.get('fees') as FormArray;
  }

  private ngUnsubscribe = new Subject<void>();

  public constructor(
    private formBuilder: FormBuilder,
    private tariffOfChargesService: TariffOfChargesService,
    private toastService: ToastService,
    public modalService: ModalService,
    private modalStateService: ModalStateService,
    public lendingTypeService: LendingTypeService,
  ) {}

  public ngOnInit(): void {
    this.tariffOfChargesService.get(this.lenderNameId).subscribe((res) => {
      this.createFeeForm(res);
    });
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private createFeeForm(model: TariffOfCharges): void {
    this.model = model;

    const lendingTypeNames = model.supportedLendingTypes.map(lt => lt.name);
    this.lendingTypeOptions = model.supportedLendingTypes.length === 1
      ? [...lendingTypeNames]
      : ['All', ...lendingTypeNames];

    this.additionalFeeOptions = [
      ...new Set(model.defaultFees.map(f => f.name)),
    ].sort((a, b) => a.localeCompare(b));

    this.form = this.formBuilder.group({
      fees: this.formBuilder.array([]),
    });

    model.fees
      .sort((a, b) => a.name.localeCompare(b.name))
      .forEach(fee => {
        const feeGroup = this.createFeeRow(fee);
        this.feeArray.push(feeGroup);
      });

    if (this.disabled) {
      this.form.disable({ onlySelf: false, emitEvent: false });
    }

    this.form.valueChanges
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => this.unsavedChangesChange.emit(true));
  }

  private createFeeRow(fee: Fee): FormGroup {
    const initialLendingType = fee.lendingTypeId
      ? this.model?.supportedLendingTypes.find(lt => lt.id === fee.lendingTypeId)?.name
        ?? this.lendingTypeOptions[0]
      : this.lendingTypeOptions[0];

    // Convert the existing fee.feeConfiguration object
    // into an array of selected keys.
    const selectedMortgageTypes = fee.feeConfiguration && fee.applicable
      ? Object.keys(fee.feeConfiguration).filter(key => fee.feeConfiguration[key])
      : [];
    const feeConfigurationControl = this.formBuilder.control<string[]>(selectedMortgageTypes);

    const group = this.formBuilder.group({
      applicable: { value: fee.applicable && !fee.constant, disabled: fee.constant },
      name: fee.name,
      constant: fee.constant,
      amount: this.formBuilder.control<number | null>(
        { value: fee.applicable ? fee.amount : null, disabled: fee.constant },
        [
          Validators.required,
          Validators.min(0.01),
          Validators.max(99999.99),
        ],
      ),
      whenPayable: { value: fee.whenPayable, disabled: fee.whenPayableIsConstant },
      whenPayableIsConstant: fee.whenPayableIsConstant,
      addToLoan: fee.applicable && fee.addToLoan,
      lendingType: this.formBuilder.nonNullable.control<string>(
        { value: initialLendingType, disabled: fee.constant },
      ),
      feeConfiguration: feeConfigurationControl,
    },
    {
      validators: [
        (ctrl: AbstractControl) => {
          if (!ctrl.get('applicable')?.value) {
            return null;
          }
          const amountVal = ctrl.get('amount')?.value;
          if (!fee.constant) {
            if (!amountVal || amountVal <= 0) {
              return { requiredAmount: true };
            }
          }
          return null;
        },
        (ctrl: AbstractControl) => {
          if (!ctrl.get('applicable')?.value || !ctrl.get('constant')?.value === false) {
            return null;
          }
          const selected = ctrl.get('feeConfiguration')?.value as string[];
          return (selected && selected.length > 0) ? null : { requiredMortgageType: true };
        },
        (ctrl: AbstractControl) => {
          const arr = this.feeArray;
          const conflictIdx = this.uniqueFeeValidator(arr);
          if (!conflictIdx?.length) {
            return null;
          }
          const rowIndex = arr.controls.findIndex(c => c === ctrl);
          return conflictIdx.includes(rowIndex)
            ? { duplicateConfiguration: true }
            : null;
        },
      ],
    });

    // Disable readonly fields
    this.toggleFeeControls(group, fee.applicable, fee);

    group.get('applicable')?.valueChanges
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((isApplicable: boolean) => {
        this.toggleFeeControls(group, isApplicable, fee);
        const defaultFee = this.model?.defaultFees.find(f => f.name === fee.name);

        if (isApplicable && defaultFee && this.model) {
          // restore all of the defaultFee values
          group.get('amount')?.setValue(defaultFee.amount);
          group.get('whenPayable')?.setValue(defaultFee.whenPayable);
          group.get('addToLoan')?.setValue(defaultFee.addToLoan);
          feeConfigurationControl.setValue(
            Object.keys(defaultFee.feeConfiguration)
              .filter(key => defaultFee.feeConfiguration[key] as boolean),
          );
          const defaultLendingTypeName = defaultFee.lendingTypeId === null
            ? this.lendingTypeOptions[0] // "All"
            : this.model.supportedLendingTypes.find(lt => lt.id === defaultFee.lendingTypeId)?.name
              ?? this.lendingTypeOptions[0];
          group.get('lendingType')?.setValue(defaultLendingTypeName);

          group.markAsPristine();
          group.markAsUntouched();
        } else {
          group.get('amount')?.setValue(null);
          group.get('addToLoan')?.setValue(false);
          feeConfigurationControl.setValue([]);
        }
      });

    group.get('lendingType')?.valueChanges
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        const myName = group.get('name')?.value;
        // revalidate every row whose name matches this row name
        this.feeArray.controls
          .filter(r => r.get('name')?.value === myName)
          .forEach(r => {
            // re‑run row‐level validators
            r.updateValueAndValidity();
          });
      });

    group.valueChanges
      .pipe(takeUntil(this.ngUnsubscribe))
      .pipe(
        debounceTime(200),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        startWith(group.value),
        pairwise(),
      )
      .subscribe(([prev, next]) => {
        if (prev.lendingType === 'All' && next.lendingType !== 'All' && next.applicable === true) {
          const idx = this.feeArray.controls.indexOf(group);
          const nextRaw = (group as FormGroup).getRawValue();
          this.openLendingTypeModal(nextRaw, idx);
        }
      });

    feeConfigurationControl.valueChanges
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        const feeName = group.get('name')?.value;
        // revalidate every row whose name matches this row name
        this.feeArray.controls
          .filter(r => r.get('name')?.value === feeName)
          .forEach(r => {
            // re‑run row‐level validators
            r.updateValueAndValidity();
          });
      });

    return group;
  }

  private toggleFeeControls(group: FormGroup, applicable: boolean, fee: Fee): void {
    fee.applicable = applicable;
    const controls = fee.constant
      ? ['amount', 'lendingType', 'feeConfiguration']
      : ['amount', 'whenPayable', 'addToLoan', 'lendingType', 'feeConfiguration'];
    controls.forEach(control => {
      const ctrl = group.get(control);
      if (!fee.constant && fee.applicable) {
        ctrl?.enable({ emitEvent: false });
      } else {
        ctrl?.disable({ emitEvent: false });
      }
    });
  }

  private uniqueFeeValidator(feeArray: FormArray): number[] {
    if (!this.model) {
      return [];
    }

    const rows = feeArray.getRawValue() as {
      name: string;
      lendingType: string;
      feeConfiguration: string[];
    }[];

    const conflicts = new Set<number>();
    const names = Array.from(new Set(rows.map(r => r.name)));

    for (const name of names) {
      const sameNameRows = rows
        .map((r, i) => ({ row: r, idx: i }))
        .filter(x => x.row.name === name);

      for (let i = 0; i < sameNameRows.length; i++) {
        for (let j = i + 1; j < sameNameRows.length; j++) {
          const { row: a, idx: ia } = sameNameRows[i];
          const { row: b, idx: ib } = sameNameRows[j];

          if (a.lendingType !== 'All' && b.lendingType !== 'All' && a.lendingType !== b.lendingType) {
            continue;
          }

          const cfgA = a.feeConfiguration;
          const cfgB = b.feeConfiguration;
          const overlap = cfgA.some(item => cfgB.includes(item));

          if (overlap) {
            conflicts.add(ia);
            conflicts.add(ib);
          }
        }
      }
    }

    return Array.from(conflicts);
  }

  public hasDuplicateConfigError(idx: number): boolean {
    const arrErr = this.form.get('fees')?.errors?.duplicateConfiguration as
      | { name: string; lendingType: number | null }
      | undefined;

    if (!arrErr) {
      return false;
    }

    const row = this.feeArray.at(idx).value;
    return row.name === arrErr.name && row.lendingType === arrErr.lendingType;
  }

  public getFeeControl(i: number, field: string): AbstractControl | null {
    return (this.feeArray.at(i) as FormGroup).get(field);
  }

  public hasConfigError(idx: number): boolean {
    const row = this.feeArray.at(idx);
    return row.hasError('requiredMortgageType')
        || row.hasError('duplicateConfiguration');
  }

  public getFeeTooltip(feeName: string): string {
    return FEE_TOOLTIPS[feeName.trim()] || '';
  }

  public isDisabled(feeGroup: AbstractControl, path: string): boolean {
    return feeGroup.get(path)?.disabled ?? false;
  }

  public canRemoveFee(idx: number): boolean {
    if (this.disabled) {
      return false;
    }

    const name = this.feeArray.at(idx).value.name;
    const allForName = this.feeArray.controls.filter(c => c.value.name === name);
    // only allow removal only if more than one fee of the same type exists
    return allForName.length > 1;
  }

  public onSubmit(): void {
    if (this.disabled) {
      return;
    }
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();
    this.feeArray.updateValueAndValidity();

    if (!this.form.valid) {
      scrollIntoView('.is-invalid');
      return;
    }

    const formFees : Fee[] = this.form.getRawValue().fees.map((x: any) => {
      // Build the feeConfiguration with default false values
      const feeConfigObj : FeeConfiguration = {
        purchase: false,
        remortgage: false,
        productTransfer: false,
        furtherAdvance: false,
        existingCustomer: false,
        secondCharge: false,
      };
      // For each option, set to true if it is selected in the feeConfiguration array
      if (Array.isArray(x.feeConfiguration)) {
        this.feeConfigOptions.forEach(option => {
          if (x.feeConfiguration.includes(option.value)) {
            feeConfigObj[option.value] = true;
          }
        });
      }

      const lendingTypeId =
        x.lendingType === 'All'
          ? undefined
          : this.model?.supportedLendingTypes.find(lt => lt.name === x.lendingType)?.id;

      const feeObj: Fee = {
        applicable: x.applicable,
        constant: x.constant,
        name: x.name,
        amount: x.applicable ? x.amount : null,
        feeConfiguration: feeConfigObj,
        lendingTypeId,
        whenPayable: x.whenPayable,
        whenPayableIsConstant: x.whenPayableIsConstant,
        addToLoan: x.addToLoan,
      };
      return feeObj;
    });

    const request = {
      ...this.form.value,
      fees: [...formFees],
    };

    this.tariffOfChargesService.save(this.lenderNameId, request).subscribe({
      next: () => {
        this.toastService.success('Tariff successfully updated');
        this.unsavedChangesChange.emit(false);
      },
    });
  }

  public openLendingTypeModal(feeRowData: any, index: number) {
    return this.modalService
      .open<never>({
        title: 'Apply Separate Fee Dependent On Lending Type',
        template: this.lendingTypeModalTemplateRef,
        size: 'md',
        data: { feeForm: feeRowData, index },
      })
      .then(() => null, () => null);
  }

  public duplicateFee(): void {
    if (!this.model) {
      return;
    }

    const feeForm = this.modalStateService.options.data.feeForm as any;
    const index  = this.modalStateService.options.data.index as number;

    // Insert one new row per supported lending type
    let offset = 1;
    this.model.supportedLendingTypes
      .filter(type => type.name !== feeForm.lendingType)
      .forEach(type => {
        const feeConfig: FeeConfiguration = {
          purchase: feeForm.feeConfiguration.includes('purchase'),
          remortgage: feeForm.feeConfiguration.includes('remortgage'),
          productTransfer: feeForm.feeConfiguration.includes('productTransfer'),
          furtherAdvance: feeForm.feeConfiguration.includes('furtherAdvance'),
          existingCustomer: feeForm.feeConfiguration.includes('existingCustomer'),
          secondCharge: feeForm.feeConfiguration.includes('secondCharge'),
        };
        const newFee: Fee = {
          ...feeForm,
          lendingTypeId: type.id,
          feeConfiguration: feeConfig,
        };
        // insert it just below the original row, bumping down for each
        this.addAdditionalLine(newFee, index + offset);
        offset++;
      });
  }

  public setDefaultLendingType(): void {
    const idx = this.modalStateService.options.data.index;
    this.feeArray.at(idx).patchValue({ lendingType: 'All' });
  }

  public addAdditionalLine(newFee: Fee, index: number): void {
    const feeGroup = this.createFeeRow(newFee);
    this.feeArray.insert(index, feeGroup);

    // Scroll to new line and play animation
    // wait for the DOM to update, then scroll the new row into view
    setTimeout(() => {
      // eslint-disable-next-line spellcheck/spell-checker
      const tableHtml = this.tableContainer.nativeElement.querySelector('tbody');
      if (!tableHtml) {
        return;
      }
      const rowsHtml = tableHtml.getElementsByTagName('tr');
      const newRowHtml = rowsHtml[index];
      if (newRowHtml) {
        // Scroll to new row
        newRowHtml.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });

        const cells = Array.from(newRowHtml.querySelectorAll('td')) as HTMLElement[];
        cells.forEach(cell => {
          // Grab exact computed cell background
          const origBg = getComputedStyle(cell).backgroundColor;
          // Animate from green → origBg over 2s
          cell.animate([
            { backgroundColor: '#aeda8a' },
            { backgroundColor: origBg },
          ], {
            duration: 2000,
            easing:   'ease-out',
          });
        });
      }
    }, 0);
  }

  public removeAdditionalLine(index: number): void {
    this.feeArray.removeAt(index);
    this.form.markAsDirty();
  }

  public onAdditionalFeeSelected(selectedFee: string): void {
    if (!this.model || !selectedFee?.trim()) {
      return;
    }

    const defaultFee = this.model.defaultFees.find(f => f.name === selectedFee);
    if (!defaultFee) {
      return;
    }

    const newFee: Fee = {
      ...defaultFee,
      applicable: true,
    };

    let lastIndex = this.feeArray.length - 1;
    for (let i = 0; i < this.feeArray.length; i++) {
      if (this.feeArray.at(i).value.name === selectedFee) {
        lastIndex = i;
      }
    }

    this.addAdditionalLine(newFee, lastIndex + 1);
  }

  public formatMortgageTypeLabel(key: string): string {
    switch (key) {
      case 'purchase': return 'Purchase';
      case 'remortgage': return 'Remortgage';
      case 'productTransfer': return 'Product Transfer';
      case 'furtherAdvance': return 'Further Advance';
      case 'existingCustomer': return 'Existing Customer';
      case 'secondCharge': return 'Second Charge';
      default: return key;
    }
  }
}
